import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import SEO from "../components/SEO"
import "../styles/layouts/LawyerProfilePage.scss"
import axios from "axios"
import { useFilters } from "../hooks/useFilters"
import userService from "../services/userService"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import ReviewModal from "../components/ReviewModal"
import { fetchReviews, submitReview, fetchOrganisations } from "../services/organisationService"
import ClaimListingModal from "../components/claim/ClaimListingModal"
import AppSidebar from '../components/common/AppSidebar'
import LawyerCard from "../components/lawyer/LawyerCard"
import { useSearchState } from "../contexts/SearchStateContext"
import { deduplicateResults } from '../utils/deduplicationUtils'

// Import our component library
import {
  SearchDropdowns,
  ProfileHeader,
  InformationCards,
  ContactSection,
  ReviewsSection,
  CallToAction,
  BookingModal,
  getReviewCountsByRating,
  getReviewSources,
  getReviewCountsBySource,
  getReviewSource,
  getReviewRating,
  getReviewTimestamp
} from "../components/LawyerProfile"

import EnquiryModal from '../components/EnquiryModal';

// Keep only the accreditations array as it's used for UI purposes
const accreditations = [
  { id: 1, name: "SRA", imgSrc: require("../assets/images/user/image 5.png"), alt: "Solicitors Regulation Authority" },
  { id: 2, name: "Lexcel", imgSrc: require("../assets/images/user/image 6.png"), alt: "Lexcel Quality Mark" },
  { id: 3, name: "CQS", imgSrc: require("../assets/images/user/image 7.png"), alt: "Conveyancing Quality Scheme" },
  { id: 4, name: "CQC", imgSrc: require("../assets/images/user/image 8.png"), alt: "Care Quality Commission" },
  { id: 5, name: "Google", imgSrc: require("../assets/images/user/image 9.png"), alt: "Google Rating 5.0" },
  { id: 6, name: "Trustpilot", imgSrc: require("../assets/images/user/image 10.png"), alt: "Trustpilot Rating" },
]

// Number of results to show per page (matching UserPage)
const RESULTS_PER_PAGE = 6;

const LawyerProfilePage = () => {
  // Extract URL parameters
  const { id, solicitorName, city, category } = useParams();
  
  // Detect if solicitorName is actually a numeric ID
  const isNumericId = solicitorName && /^\d+$/.test(solicitorName);
  const actualId = id || (isNumericId ? solicitorName : null);
  const actualSolicitorName = isNumericId ? null : solicitorName;

  const navigate = useNavigate()
  const location = useLocation()
  const { saveSearchState } = useSearchState();
  const [lawyer, setLawyer] = useState(null)
  const [reviews, setReviews] = useState({ reviews: [], stats: null })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showBookingModal, setShowBookingModal] = useState(false)
  const [bookingForm, setBookingForm] = useState({
    name: '',
    phone: '',
    lawyerId: '',
    lawyerEmail: '',
    legalArea: '',
    description: ''
  })
  const [currentUser, setCurrentUser] = useState(null)
  const [showClaimModal, setShowClaimModal] = useState(false)
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [bookingFormData, setBookingFormData] = useState({
    name: '',
    phone: ''
  })
  
  // Add review filter state
  const [reviewFilter, setReviewFilter] = useState('all')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [sortFilter, setSortFilter] = useState('newest')
  const [sortCounter, setSortCounter] = useState(0) // Add counter to force re-render
  const [isSorting, setIsSorting] = useState(false) // Add sorting indicator state
  // filteredReviews is now computed using useMemo below

  // Update search states to match UserPage implementation
  const [searchResults, setSearchResults] = useState([])
  const [isSearchMode, setIsSearchMode] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [searchPerformed, setSearchPerformed] = useState(false)
  const [totalResults, setTotalResults] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [visibleResults, setVisibleResults] = useState([])

  // Get practice areas and cities from useFilters hook - same as UserPage
  const { cities, practiceAreas } = useFilters()
  
  // Update booking form when lawyer data is loaded
  useEffect(() => {
    if (lawyer) {
      setBookingForm(prev => ({
        ...prev,
        lawyerId: lawyer.id,
        lawyerEmail: lawyer?.contactDetails?.email || lawyer?.email || 'info@zahoot.co.uk'
      }));
    }
  }, [lawyer]);

  // Pre-fill booking form with user data when modal opens
  useEffect(() => {
    if (showBookingModal && currentUser) {
      setBookingForm(prev => ({
        ...prev,
        name: currentUser.name || currentUser.fullName || prev.name,
        phone: currentUser.phone || prev.phone
      }));
    }
  }, [showBookingModal, currentUser]);

  // Clean up URL if it has query parameters
  useEffect(() => {
    const currentUrl = window.location.href;
    if (currentUrl.includes('?')) {
      // Get the pathname without query parameters
      const cleanUrl = window.location.pathname;
      
      // Remove all query parameters by replacing with clean URL
      window.history.replaceState({}, '', cleanUrl);
    }
  }, []);

  // Parse query parameters from URL
  const queryParams = new URLSearchParams(location.search)
  const serviceParam = queryParams.get("area")
  const areaParam = queryParams.get("city")
  const specificParam = queryParams.get("budget")

  // Transform practice areas into the format needed for dropdown - same as UserPage
  const serviceOptions =
    practiceAreas?.map((area) => ({
      value: area.toLowerCase().replace(/\s+/g, "-"),
      label: area,
    })) || []

  // Create a flat list of cities for the dropdown - same as UserPage
  const allCities = cities?.flatMap((group) => group.cities || []) || []
  const areaOptions =
    allCities.map((city) => ({
      value: city.toLowerCase().replace(/\s+/g, "-"),
      label: city,
    })) || []

  // Specifics options for the third dropdown - with a way to handle user-provided values
  const getSpecificOptions = (specificParam) => {
    const baseOptions = [
      { value: "certificates", label: "Looking for certificates" },
      { value: "consultation", label: "Initial consultation" },
      { value: "representation", label: "Full representation" },
    ];
    
    // If we have a custom specific param that doesn't match any of our predefined options,
    // add it as a custom option
    if (specificParam && !baseOptions.some(opt => 
      opt.label.toLowerCase() === specificParam.toLowerCase() ||
      opt.value.toLowerCase() === specificParam.toLowerCase())) {
      
      return [
        ...baseOptions,
        { value: specificParam.toLowerCase().replace(/\s+/g, "-"), label: specificParam }
      ];
    }
    
    return baseOptions;
  };

  const specificOptions = getSpecificOptions(specificParam);

  // Find matching options from URL parameters with improved matching
  const findMatchingOption = (options, paramValue) => {
    if (!options || options.length === 0 || !paramValue) return null

    // Convert paramValue to lowercase for easier comparison
    const searchValue = paramValue.toLowerCase().trim()
    
    // For "general" service, which may be named differently in the options
    if (searchValue === "general") {
      const generalOptions = options.filter(opt => 
        opt.label.toLowerCase().includes("general") || 
        opt.label.toLowerCase().includes("all") ||
        opt.label.toLowerCase() === "any"
      )
      if (generalOptions.length > 0) return generalOptions[0]
    }

    // First try exact match
    let match = options.find(
      (opt) => 
        opt.label.toLowerCase() === searchValue || 
        opt.value.toLowerCase() === searchValue
    )
    
    // If no exact match, try partial matching
    if (!match) {
      match = options.find(
        (opt) => 
          opt.label.toLowerCase().includes(searchValue) || 
          searchValue.includes(opt.label.toLowerCase())
      )
    }

    return match || null
  }

  // Initialize with values from URL params
  const [selectedService, setSelectedService] = useState(null)
  const [selectedArea, setSelectedArea] = useState(null)
  const [selectedSpecific, setSelectedSpecific] = useState(null)

  // Update dropdown selections when filter data loads
  useEffect(() => {
    if (serviceOptions.length > 0) {
      // Try to match with 'service' param first, fall back to 'area' param for backward compatibility
      const serviceParamValue = new URLSearchParams(window.location.search).get('service') || serviceParam;
      if (serviceParamValue) {
        const newService = findMatchingOption(serviceOptions, serviceParamValue);
        if (newService && JSON.stringify(newService) !== JSON.stringify(selectedService)) {
          setSelectedService(newService);
        }
      }
    }

    if (areaOptions.length > 0) {
      // Try to match with 'area' param first, fall back to 'city' param for backward compatibility
      const areaParamValue = new URLSearchParams(window.location.search).get('area') || areaParam;
      if (areaParamValue) {
        const newArea = findMatchingOption(areaOptions, areaParamValue);
        if (newArea && JSON.stringify(newArea) !== JSON.stringify(selectedArea)) {
          setSelectedArea(newArea);
        }
      }
    }

    if (specificOptions.length > 0) {
      // Try to match with 'specific' param first, fall back to 'budget' param for backward compatibility
      const specificParamValue = new URLSearchParams(window.location.search).get('specific') || specificParam;
      if (specificParamValue) {
        const newSpecific = findMatchingOption(specificOptions, specificParamValue);
        if (newSpecific && JSON.stringify(newSpecific) !== JSON.stringify(selectedSpecific)) {
          setSelectedSpecific(newSpecific);
        }
      }
    }
  }, [serviceOptions, areaOptions, specificOptions, serviceParam, areaParam, specificParam]);

  // Create refs to track current dropdown selections
  const currentServiceRef = useRef(null);
  const currentAreaRef = useRef(null);
  const currentSpecificRef = useRef(null);

  // Keep refs in sync with state values
  useEffect(() => {
    currentServiceRef.current = selectedService;
  }, [selectedService]);

  useEffect(() => {
    currentAreaRef.current = selectedArea;
  }, [selectedArea]);

  useEffect(() => {
    currentSpecificRef.current = selectedSpecific;
  }, [selectedSpecific]);

  // Function to update URL parameters without triggering updates
  const updateURLParams = (paramName, value) => {
    console.log(`Updating URL param: ${paramName} to value: ${value}`);
    const params = new URLSearchParams(window.location.search);
    
    // Only update URL if the value has changed
    if (value) {
      params.set(paramName, value);
    } else {
      // Value is null or undefined, remove the parameter
      params.delete(paramName);
      console.log(`Cleared parameter ${paramName} from URL`);
    }
    
    // Update URL without reloading the page
    const newRelativePathQuery = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
    console.log(`New URL: ${newRelativePathQuery}`);
    window.history.pushState(null, '', newRelativePathQuery);
  }

  // Function to convert text to URL-friendly slug
  const createSlug = (text) => {
    if (!text) return '';
    return text.toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Event handlers for dropdown changes
  const handleServiceChange = useCallback((option) => {
    console.log("Service changed to:", option);
    
    // Just update the state, ref will be updated in the useEffect
    setSelectedService(option);
    
    // For query parameters
    updateURLParams('service', option?.label || null);
    
    // Force update SEO-friendly path format immediately
    if (!isSearchMode) {
      // We need to rebuild the path when service changes
      const nameSlug = lawyer?.name ? createSlug(lawyer.name) : '';
      
      if (!option) {
        // If service is cleared, remove it from path
        const locationSlug = selectedArea?.label ? createSlug(selectedArea.label) : 
                             lawyer?.location ? createSlug(lawyer.location) : '';
                             
        // If we have a location, use that in path
        if (locationSlug) {
          const newPath = `/solicitor/${locationSlug}/${nameSlug}`;
          console.log(`Service cleared, updating path to: ${newPath}`);
          
          // Check if we're on a URL that starts with a service category
          const currentPath = window.location.pathname;
          if (currentPath.startsWith('/') && currentPath.split('/').length > 2 && 
              currentPath.split('/')[1] !== 'solicitor') {
            // We're on a path like /{service}/solicitor/...
            // Force a redirect instead of just updating history
            window.location.href = newPath;
            return;
          }
          
          window.history.pushState({ lawyerId: lawyer.id }, '', newPath);
        } else {
          // Just use the lawyer name
          const newPath = `/solicitor/${nameSlug}`;
          console.log(`Service cleared, updating path to: ${newPath}`);
          
          // Check if we're on a URL that starts with a service category
          const currentPath = window.location.pathname;
          if (currentPath.startsWith('/') && currentPath.split('/').length > 2 && 
              currentPath.split('/')[1] !== 'solicitor') {
            // We're on a path like /{service}/solicitor/...
            // Force a redirect instead of just updating history
            window.location.href = newPath;
            return;
          }
          
          window.history.pushState({ lawyerId: lawyer.id }, '', newPath);
        }
      } else {
        // Normal SEO path update with service included
        updateUrlPathWithSEO(option, selectedArea, selectedSpecific);
      }
    }
  }, [isSearchMode, lawyer, selectedArea, selectedSpecific])

  const handleAreaChange = useCallback((option) => {
    console.log("Area changed to:", option);
    
    // Just update the state, ref will be updated in the useEffect
    setSelectedArea(option);
    
    // For query parameters
    updateURLParams('area', option?.label || null);
    
    // Force update SEO-friendly path format immediately
    if (!isSearchMode) {
      // We need to rebuild the path when area changes
      const nameSlug = lawyer?.name ? createSlug(lawyer.name) : '';
      
      if (!option) {
        // If area is cleared
        if (selectedService) {
          const serviceSlug = createSlug(selectedService.label);
          const newPath = `/${serviceSlug}/solicitor/${nameSlug}`;
          console.log(`Area cleared, updating path to: ${newPath}`);
          window.history.pushState({ lawyerId: lawyer.id }, '', newPath);
        } else {
          // Just use the lawyer name
          const newPath = `/solicitor/${nameSlug}`;
          console.log(`Area cleared, updating path to: ${newPath}`);
          window.history.pushState({ lawyerId: lawyer.id }, '', newPath);
        }
      } else {
        // Normal SEO path update with area included
        updateUrlPathWithSEO(selectedService, option, selectedSpecific);
      }
    }
  }, [isSearchMode, lawyer, selectedService, selectedSpecific])

  const handleSpecificChange = useCallback((option) => {
    console.log("Specific changed to:", option);
    
    // Just update the state, ref will be updated in the useEffect
    setSelectedSpecific(option);
    
    // Handle clearing the selection case - use specific as the URL parameter name
    updateURLParams('specific', option?.label || null);
  }, [])

  // Update the URL path with SEO-friendly format
  const updateUrlPathWithSEO = (service, area, specific) => {
    if (!lawyer) return;

    // Get service and location from params
    const serviceLabel = service?.label;
    const locationLabel = area?.label || lawyer.location;
    
    // Create slug from lawyer name
    const nameSlug = lawyer.name.toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    // Determine which URL format to use
    let pathname;
    if (serviceLabel && locationLabel) {
      // If we have both practice area and city, use the full format
      const serviceSlug = serviceLabel.toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      const locationSlug = locationLabel.toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      pathname = `/${serviceSlug}/solicitor/${locationSlug}/${nameSlug}`;
    } else if (locationLabel) {
      // If we only have location/city
      const locationSlug = locationLabel.toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      pathname = `/solicitor/${locationSlug}/${nameSlug}`;
    } else {
      // Just the solicitor name
      pathname = `/solicitor/${nameSlug}`;
    }
    
    console.log(`Updating URL path to: ${pathname}`);
    
    // Update the URL without reloading the page (no query params)
    window.history.pushState(
      { 
        // Preserve important state data including lawyer ID
        lawyerId: lawyer.id,
        area: serviceLabel,
        city: locationLabel
      }, 
      '', 
      pathname
    );
  }

  // Function to select a solicitor from search results
  const handleSelectSolicitor = (solicitor) => {
    // Clear search mode and results
    setIsSearchMode(false);
    setSearchResults([]);
    setVisibleResults([]);
    
    // Generate the name slug
    const nameSlug = solicitor.name?.toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    // Generate location slug if available
    const locationSlug = solicitor.location?.toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    // Generate service slug if available
    const serviceSlug = selectedService?.label?.toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    // Construct the appropriate URL path
    let pathname;
    if (serviceSlug && locationSlug) {
      pathname = `/${serviceSlug}/solicitor/${locationSlug}/${nameSlug}`;
    } else if (locationSlug) {
      pathname = `/solicitor/${locationSlug}/${nameSlug}`;
    } else {
      pathname = `/solicitor/${nameSlug}`;
    }
    
    // Navigate to the solicitor profile
    navigate({
      pathname,
      state: { 
        lawyerId: solicitor.id,
        area: selectedService?.label,
        city: selectedArea?.label
      }
    });
  };

  // Function to perform solicitor search based on dropdown selections
  const handleSolicitorSearch = async () => {
    // Use current state values, not refs
    const service = selectedService;
    const area = selectedArea;
    const specific = selectedSpecific;
    
    console.log("Search triggered with:", { 
      service, 
      area, 
      specific 
    });
    
    if (!service && !area) {
      toast.error("Please select at least a service or area to search");
      return;
    }
    
    try {
      setIsSearching(true);
      setSearchPerformed(true);
      
      // Always set search mode to true, whether this is initial or subsequent search
      setIsSearchMode(true);
      
      // Reset pagination when new search is performed
      setCurrentPage(1);
      setVisibleResults([]);
      setSearchResults([]); // Clear previous search results
      
      // Update URL parameters to reflect the search
      const params = new URLSearchParams();
      
      if (service) {
        params.set('service', service.label);
      }
      
      if (area) {
        params.set('area', area.label);
      }
      
      if (specific) {
        params.set('specific', specific.label);
      }
      
      // Update URL without reloading the page
      const newRelativePathQuery = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.pushState(null, '', newRelativePathQuery);
      
      // Fetch all organizations to filter client-side
      const organisations = await fetchOrganisations();
      console.log('Fetched organisations:', organisations);
      
      // Filter based on selected service and area with more flexible matching
      let filtered = [...organisations];
      
      if (service?.label) {
        // Match service with practice areas
        filtered = filtered.filter(org => {
          if (!org.practice_areas || org.practice_areas.length === 0) {
            return service.label.toLowerCase() === 'general';
          }
          
          // Parse practice areas if it's a string
          let practiceAreas = org.practice_areas;
          if (typeof practiceAreas === 'string') {
            try {
              practiceAreas = JSON.parse(practiceAreas);
            } catch (e) {
              practiceAreas = [];
            }
          }
          
          // Check if any practice area matches the selected service
          return practiceAreas.some(pa => {
            const areaName = typeof pa === 'object' ? pa.name : pa;
            return areaName.toLowerCase() === service.label.toLowerCase();
          });
        });
      }
      
      if (area?.label) {
        // Match area with office locations
        filtered = filtered.filter(org => {
          if (!org.offices || org.offices.length === 0) {
            return false;
          }
          
          return org.offices.some(office => 
            (office.town && office.town.toLowerCase() === area.label.toLowerCase())
          );
        });
      }
      
      // Log filtered results for debugging
      console.log('Filtered results:', filtered.length);
      filtered.forEach((org, i) => {
        if (i < 5) { // Log only first 5 for brevity
          console.log(`- ${org.practice_name} (${org.id})`);
        }
      });
      
      // Update state with filtered results (no deduplication needed for standard search)
      setSearchResults(filtered);
      setTotalResults(filtered.length);
      
      // Initialize visible results with first page
      setVisibleResults(filtered.slice(0, RESULTS_PER_PAGE));
      
      // Log search completion
      console.log('Search completed with results:', filtered.length);
    } catch (error) {
      console.error("Error searching solicitors:", error);
      setError("An error occurred while searching for solicitors");
      setSearchResults([]);
      setTotalResults(0);
      setVisibleResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Function to load more results (pagination) - matching UserPage
  const handleLoadMore = () => {
    console.log('Loading more results...');
    const nextPage = currentPage + 1;
    const endIndex = nextPage * RESULTS_PER_PAGE;
    const newVisibleResults = [...visibleResults, ...searchResults.slice((nextPage - 1) * RESULTS_PER_PAGE, endIndex)];
    
    setCurrentPage(nextPage);
    setVisibleResults(newVisibleResults);
    
    console.log('Current page:', nextPage);
    console.log('Visible results:', newVisibleResults.length);
    console.log('Total results:', searchResults.length);
  };

  // Function to clear search results and reset form
  const handleClearSearch = () => {
    // Clear search state
    setIsSearchMode(false);
    setSearchResults([]);
    setVisibleResults([]);
    setSearchPerformed(false);
    setError(null);
    
    // Clear selected values
    setSelectedService(null);
    setSelectedArea(null);
    setSelectedSpecific(null);
    
    // Clear URL parameters
    window.history.pushState(null, '', window.location.pathname);
    
    // If we have a lawyer, update URL path with SEO format
    if (lawyer) {
      // Build the basic solicitor URL without service/area
      const nameSlug = lawyer.name.toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      
      const newPath = `/solicitor/${nameSlug}`;
      console.log(`Clearing all selections, new path: ${newPath}`);
      
      window.history.pushState(
        { lawyerId: lawyer.id }, 
        '', 
        newPath
      );
    }
  };

  // Function to navigate to user page with selected filters
  const handleNavigateToUserPage = () => {
    // Save the current search state to context before navigation
    console.log('Saving search state to context:', {
      selectedService: selectedService?.label,
      selectedArea: selectedArea?.label,
      selectedSpecific: selectedSpecific?.label,
      totalResults
    });
    
    // Create normalized options for saving
    const normalizedService = selectedService ? {
      value: selectedService.value || selectedService.label,
      label: selectedService.label || selectedService.value
    } : null;
    
    const normalizedArea = selectedArea ? {
      value: selectedArea.value || selectedArea.label,
      label: selectedArea.label || selectedArea.value
    } : null;
    
    const normalizedSpecific = selectedSpecific ? {
      value: selectedSpecific.value || selectedSpecific.label,
      label: selectedSpecific.label || selectedSpecific.value
    } : null;
    
    // Save fully normalized state
    saveSearchState({
      selectedService: normalizedService,
      selectedArea: normalizedArea,
      selectedSpecific: normalizedSpecific,
      totalResults,
      currentPage
    });

    const params = new URLSearchParams()

    if (selectedService) {
      params.append("area", selectedService.label)
    }

    if (selectedArea) {
      params.append("city", selectedArea.label)
    }

    if (selectedSpecific) {
      params.append("budget", selectedSpecific.label)
    }

    // Navigate to search page using standard history navigation
    navigate({ 
      pathname: "/search", 
      search: params.toString(),
      state: {
        fromLawyerProfile: true,
        lawyerId: lawyer?.id
      }
    })
  }

  // Fetch lawyer profile and reviews data when component mounts
  useEffect(() => {
    // Flag to detect if this is a manual refresh
    const isManualRefresh = window.performance && 
                          window.performance.navigation && 
                          window.performance.navigation.type === 1;
    
    const fetchLawyerData = async (retryCount = 0, maxRetries = 2) => {
      // If it's a manual refresh, we'll bypass cache
      const bypassCache = isManualRefresh;
      
      try {
        // Determine what identifier we're using to fetch data
        let identifier;
        let isSlugRequest = false;
        
        // Get ID from location state if available
        const stateId = location.state?.lawyerId;
        
        if (actualId) {
          // Using legacy ID-based URL or numeric ID detected in solicitorName
          identifier = actualId;
          console.log(`Using ID from URL param: ${identifier}`);
        } else if (stateId) {
          // Using ID from location state (passed during navigation)
          identifier = stateId;
          console.log(`Using ID from location state: ${identifier}`);
        } else if (actualSolicitorName) {
          // Using new SEO-friendly URL structure
          identifier = actualSolicitorName;
          isSlugRequest = true;
          console.log(`Using solicitor name as slug: ${identifier}`);
          
          // We'll pass additional parameters to the API if we have them
          if (city || category) {
            console.log(`Additional parameters - City: ${city}, Category: ${category}`);
          }
        } else {
          throw new Error("No valid identifier found in URL parameters");
        }
        
        console.log(`Attempting to fetch lawyer data with identifier: ${identifier}${retryCount > 0 ? ` (retry ${retryCount}/${maxRetries})` : ''}${bypassCache ? ' (cache bypass)' : ''}`)
        setLoading(true)

        // Get the API base URL from environment
        const API_URL = process.env.REACT_APP_API_URL || '';

        // Try to fetch with direct API call
        let lawyerData
        let userData = null

        // Multiple fetch attempts with different endpoints
        try {
          // Clear any previous errors on retry
          if (retryCount > 0) {
            setError(null);
          }
          
          // If we're using a slug-based approach
          if (isSlugRequest) {
            // First make a direct API call to the lookup endpoint with proper error logging
            console.log(`Trying direct lookup endpoint with: name=${identifier}, city=${city || ''}, category=${category || ''}`)
            
            try {
              // Construct the request URL with query parameters
              const lookupUrl = new URL(`${API_URL}/api/solicitors/lookup`);
              lookupUrl.searchParams.append('name', identifier);
              if (city) lookupUrl.searchParams.append('city', city);
              if (category) lookupUrl.searchParams.append('category', category);
              
              console.log(`Making direct API call to: ${lookupUrl.toString()}`);
              
              // Make the API call with full debugging
              const directResponse = await fetch(lookupUrl.toString(), {
                method: 'GET',
                headers: {
                  'Accept': 'application/json'
                }
              });
              
              // Log response status for debugging
              console.log(`Direct lookup response status: ${directResponse.status}`);
              
              // If response is not OK, throw detailed error
              if (!directResponse.ok) {
                const errorData = await directResponse.json().catch(() => ({}));
                console.error('Error response data:', errorData);
                throw new Error(`Lookup failed with status ${directResponse.status}: ${errorData.error || 'Unknown error'}`);
              }
              
              // Parse the successful response
              const responseData = await directResponse.json();
              lawyerData = responseData;
              console.log("Direct lookup successful:", lawyerData);
            } catch (directLookupError) {
              console.error("Error with direct lookup:", directLookupError.message);
              
              // Try the regular lookup approach
              try {
                console.log(`Trying regular slug-based endpoint with axios`);
                const slugResponse = await axios.get(`${API_URL}/api/solicitors/lookup`, {
                  params: {
                    name: identifier,
                    ...(city && { city }),
                    ...(category && { category })
                  }
                });
                lawyerData = slugResponse.data;
                console.log("Slug-based lookup response:", lawyerData);
              } catch (slugError) {
                console.error("Error with slug-based lookup:", slugError.message);
                if (slugError.response) {
                  console.error("Response data:", slugError.response.data);
                  console.error("Response status:", slugError.response.status);
                }
                // If slug-based approach fails, fall back to other approaches
              }
            }
          }
          
          // If we don't have data yet, try the other approaches
          if (!lawyerData) {
            // Check if we have an ID from URL or state to use for direct ID lookups
            const directId = actualId || stateId;
            
            if (directId) {
              // Try direct ID-based lookup first if we have an ID
              console.log(`Trying direct ID lookup with: ${directId}`);
              
              try {
                // First try organization endpoint with ID
                console.log(`Trying organization endpoint: ${API_URL}/api/organisations/${directId}`);
                const orgResponse = await axios.get(`${API_URL}/api/organisations/${directId}`);
                lawyerData = orgResponse.data;
                console.log("Organization endpoint response:", lawyerData);
              } catch (orgError) {
                console.error("Error with organization endpoint:", orgError.message);
                
                // If that fails, try solicitor endpoint with ID
                try {
                  console.log(`Trying solicitor endpoint: ${API_URL}/api/solicitors/${directId}`);
                  const solicitorResponse = await axios.get(`${API_URL}/api/solicitors/${directId}`);
                  lawyerData = solicitorResponse.data;
                  console.log("Solicitor endpoint response:", lawyerData);
                } catch (solicitorError) {
                  console.error("Error with solicitor endpoint:", solicitorError.message);
                  // Continue to other fallbacks
                }
              }
            }
            
            // If still no data, try user profile endpoint
            if (!lawyerData) {
              // Try with user profile endpoint first for legal professionals
              console.log(`Trying user profile endpoint: ${API_URL}/api/users/${identifier}/profile`)
              try {
                const userResponse = await axios.get(`${API_URL}/api/users/${identifier}/profile`)
                userData = userResponse.data
                console.log("User profile API response:", userData)
                
                // If this user has a claimed organization, use that
                if (userData.claimedOrganisation) {
                  lawyerData = userData.claimedOrganisation
                  console.log("Using claimed organization data:", lawyerData)
                } else {
                  // If no claimed organization, let's create a minimal lawyer profile from user data
                  lawyerData = {
                    id: userData.id,
                    practice_name: userData.name || userData.username,
                    isVerified: false,
                    user_profile: true, // Flag to indicate this is a user profile without org data
                    created_at: userData.created_at
                  }
                  console.log("Created minimal lawyer profile from user data:", lawyerData)
                }
              } catch (userError) {
                console.error("Error with user profile endpoint:", userError.message)
                
                // If that fails, try with the standard endpoint (expects slug)
                try {
                  console.log(`Trying standard endpoint: ${API_URL}/api/solicitors/${identifier}`);
                  const response = await axios.get(`${API_URL}/api/solicitors/${identifier}`);
                  lawyerData = response.data;
                  console.log("API response:", lawyerData);
                } catch (error) {
                  console.error("Error with standard endpoint:", error.message);

                  // If that fails, try a different approach - the ID might be numeric
                  // and we might need to use a different endpoint
                  try {
                    console.log(`Trying alternative endpoint: ${API_URL}/api/organisations/${identifier}`);
                    const altResponse = await axios.get(`${API_URL}/api/organisations/${identifier}`);
                    lawyerData = altResponse.data;
                    console.log("Alternative API response:", lawyerData);
                  } catch (altError) {
                    console.error("Error with alternative endpoint:", altError.message);

                    // As a last resort, try to fetch all organisations and find by ID or name
                    try {
                      console.log("Trying to fetch all organisations and filter");
                      const allOrgsResponse = await axios.get(`${API_URL}/api/solicitors`);
                      const allOrgs = allOrgsResponse.data;

                      // Find the organisation by ID or name
                      const foundOrg = allOrgs.find((org) => {
                        // Try to match by ID
                        if (org.id === Number.parseInt(identifier) || org.id === identifier) {
                          return true;
                        }
                        
                        // Try direct ID match if we have an ID in state
                        if (stateId && (org.id === Number.parseInt(stateId) || org.id === stateId)) {
                          return true;
                        }
                        
                        // Try to match by slug
                        const orgNameSlug = org.practice_name?.toLowerCase().replace(/\s+/g, '-');
                        if (orgNameSlug === identifier?.toLowerCase()) {
                          return true;
                        }
                        
                        // Try to match by city if provided
                        if (city && org.offices && org.offices.length > 0) {
                          const cityMatches = org.offices.some(office => {
                            const officeCitySlug = office.town?.toLowerCase().replace(/\s+/g, '-');
                            return officeCitySlug === city?.toLowerCase();
                          });
                          
                          if (cityMatches && orgNameSlug === identifier?.toLowerCase()) {
                            return true;
                          }
                        }
                        
                        // Try to match by practice area if category is provided
                        if (category && org.practice_areas) {
                          let practiceAreas = [];
                          try {
                            practiceAreas = Array.isArray(org.practice_areas)
                              ? org.practice_areas
                              : JSON.parse(org.practice_areas || '[]');
                          } catch (e) {
                            console.error('Error parsing practice areas:', e);
                          }
                          
                          const categoryMatches = practiceAreas.some(area => {
                            const areaSlug = area.toLowerCase().replace(/\s+/g, '-');
                            return areaSlug === category?.toLowerCase();
                          });
                          
                          if (categoryMatches && orgNameSlug === identifier?.toLowerCase()) {
                            return true;
                          }
                        }
                        
                        return false;
                      });
                      
                      if (foundOrg) {
                        lawyerData = foundOrg;
                        console.log("Found organisation in list:", lawyerData);
                      } else {
                        throw new Error("Organisation not found in list");
                      }
                    } catch (finalError) {
                      console.error("All attempts failed:", finalError.message);
                      throw new Error("Unable to fetch lawyer data from any available endpoint");
                    }
                  }
                }
              }
            }
          }
        } catch (err) {
          console.error("Error in fetch attempts:", err);
          throw err; // Re-throw to be caught by the outer try/catch
        }

        // Process the data if we found it
        if (lawyerData) {
          // Start with basic required fields
          const processedLawyer = {
            id: lawyerData.id,
            name: lawyerData.practice_name,
            // Ensure we get the verification status directly from the database
            isVerified: Boolean(lawyerData.is_verified),
            is_claimed: lawyerData.is_claimed || false,
            claim_status: lawyerData.claim_status || null
          }

          // If this is a user profile without org data, add the user data
          if (lawyerData.user_profile && userData) {
            processedLawyer.isUserProfile = true;
            processedLawyer.role = userData.role_name;
            processedLawyer.email = userData.email;
            processedLawyer.imageUrl = userData.image_url;
            processedLawyer.contactDetails = {
              email: userData.email,
              mainOffice: userData.name || userData.username
            };
          } else {
            // Only add location if it exists
            if (lawyerData.offices?.[0]?.town) {
              processedLawyer.location = lawyerData.offices[0].town;
            }

            // Only add rating and reviews if they exist
            if (lawyerData.average_rating) {
              processedLawyer.rating = lawyerData.average_rating;
            }
            if (lawyerData.review_count) {
              processedLawyer.reviews = lawyerData.review_count;
            }

            // Only add practice areas if they exist
            if (lawyerData.practice_areas) {
              try {
                processedLawyer.practiceAreas = Array.isArray(lawyerData.practice_areas)
                  ? lawyerData.practice_areas
                  : typeof lawyerData.practice_areas === "string" && lawyerData.practice_areas
                    ? JSON.parse(lawyerData.practice_areas)
                    : [];
              } catch (parseError) {
                console.error("Error parsing practice areas:", parseError);
                processedLawyer.practiceAreas = [];
              }
            }

            // Add authorisation data with proper field name checks
            processedLawyer.authorisation = {
              status: lawyerData.auth_status || 
                     lawyerData.authorisation_status || 
                     lawyerData.AuthorisationStatus || 
                     "Not available",
              type: lawyerData.auth_type || 
                   lawyerData.authorisation_type || 
                   lawyerData.OrganisationType || 
                   lawyerData.organisation_type || 
                   lawyerData.AuthorisationType || 
                   "Not available",
            };

            // Only add company info if it exists
            if (lawyerData.sra_number || lawyerData.company_no || lawyerData.offices) {
              processedLawyer.company = {
                registration: lawyerData.sra_number || lawyerData.company_no || "Not available",
                offices: lawyerData.offices
                  ? `${lawyerData.offices.length} Office${lawyerData.offices.length !== 1 ? "s" : ""}`
                  : "0 Offices",
              };
            } else {
              processedLawyer.company = {
                registration: "Not available",
                offices: "Not available",
              };
            }

            // Always add contact details, with "Not available" as fallback
            processedLawyer.contactDetails = {
              mainOffice: lawyerData.practice_name || "Not available",
              address: "Not available",
              city: "Not available",
              phone: "Not available",
              email: "Not available",
              website: "Not available",
            };
            
            // Update with actual data if it exists
            if (lawyerData.offices?.[0]) {
              const office = lawyerData.offices[0];
              processedLawyer.contactDetails = {
                ...processedLawyer.contactDetails,
                address: office.address_line_1 || office.address1 || "Not available",
                city: office.town ? `${office.town}${office.postcode ? ` ${office.postcode}` : ""}` : "Not available",
                phone: office.phone_number || "Not available",
                email: office.email || "Not available",
                website: office.website || "Not available",
                // Add location coordinates if available
                latitude: office.latitude,
                longitude: office.longitude,
              };
            }

            // Add all offices data to processed lawyer
            if (lawyerData.offices && lawyerData.offices.length > 0) {
              processedLawyer.offices = lawyerData.offices;
            }

            // Only add overview if it exists
            if (lawyerData.overview) {
              processedLawyer.overview = lawyerData.overview;
            }

            // Only add team photo if it exists
            if (lawyerData.team_photo) {
              processedLawyer.teamPhoto = lawyerData.team_photo;
            }

            // Only add logo if it exists
            if (lawyerData.logo_url) {
              processedLawyer.logoUrl = lawyerData.logo_url;
            }

            // Only add accreditations if they exist
            if (lawyerData.accreditations) {
              processedLawyer.accreditations = lawyerData.accreditations;
            }
            
            // Store raw data for SEO canonical URL generation
            processedLawyer.rawData = {
              name: lawyerData.practice_name,
              city: lawyerData.offices?.[0]?.town,
              categories: processedLawyer.practiceAreas,
              slug: lawyerData.practice_name?.toLowerCase().replace(/\s+/g, '-')
            };
          }

          setLawyer(processedLawyer)

          // Fetch reviews if we have an ID
          if (lawyerData.id) {
            try {
              console.log(`Fetching reviews for lawyer ID: ${lawyerData.id}${bypassCache ? ' (cache bypass)' : ''}`)
              const reviewsData = await fetchReviews(lawyerData.id, bypassCache)
              setReviews(reviewsData || { reviews: [], stats: null })
            } catch (reviewError) {
              console.error("Error fetching reviews:", reviewError)
              // Don't fail the whole page just because reviews couldn't be fetched
              setReviews({ reviews: [], stats: null })
            }
          }
        } else {
          setError("Lawyer profile not found")
        }

        setLoading(false)
      } catch (err) {
        console.error("Error fetching lawyer data:", err)
        
        // Retry mechanism for temporary failures
        if (retryCount < maxRetries) {
          console.log(`Retrying fetch (${retryCount + 1}/${maxRetries})...`);
          
          // Wait a bit before retrying (exponential backoff)
          const delay = Math.pow(2, retryCount) * 1000;
          setTimeout(() => {
            fetchLawyerData(retryCount + 1, maxRetries);
          }, delay);
          return;
        }
        
        // More robust error handling
        let errorMessage;
        try {
          // Check if there's a specific API error message
          if (err.response?.data?.error) {
            errorMessage = err.response.data.error;
          } 
          // Check for a standard error message
          else if (err.message) {
            errorMessage = err.message;
          } 
          // Fallback generic message
          else {
            errorMessage = "Failed to load lawyer profile. Please try again later.";
          }
        } catch (errorHandlingError) {
          // Ultimate fallback if error handling itself fails
          errorMessage = "Failed to load lawyer profile. Please try again later.";
        }
        
        setError(errorMessage);
        
        // Try to create a minimal lawyer profile even on error
        try {
          const displayId = solicitorName || id;
          if (displayId) {
            setLawyer({
              id: displayId,
              name: `Legal Professional #${displayId}`,
              isUserProfile: true,
              contactDetails: {
                mainOffice: `User #${displayId}`
              }
            });
          }
        } catch (fallbackError) {
          console.error("Error creating fallback lawyer object:", fallbackError);
          // If even this fails, make sure lawyer is null
          setLawyer(null);
        }
        
        setLoading(false);
      }
    }

    // Start fetching data if we have any identifier in the URL
    if (id || solicitorName) {
      fetchLawyerData();
    } else {
      setError("No valid identifier found in URL");
      setLoading(false);
    }
  }, [id, solicitorName, category, city]) // Re-run when any URL parameter changes

  // Filter reviews based on selected filter (using useMemo for performance)
  const filteredReviews = useMemo(() => {
    if (!reviews || !reviews.reviews) return [];
    
    console.log("===============================================");
    console.log("Filtering and sorting reviews:", 
      { 
        totalReviews: reviews.reviews.length, 
        ratingFilter: reviewFilter, 
        sourceFilter: sourceFilter,
        sortFilter: sortFilter,
        sortCounter: sortCounter
      }
    );
    
    let filtered = [...reviews.reviews];
    
    // Apply rating filter
    if (reviewFilter !== 'all') {
      const ratingValue = parseInt(reviewFilter);
      filtered = filtered.filter(review => Math.round(getReviewRating(review)) === ratingValue);
      console.log(`After rating filter (${reviewFilter}):`, filtered.length);
    }
    
    // Apply source filter
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(review => getReviewSource(review) === sourceFilter);
      console.log(`After source filter (${sourceFilter}):`, filtered.length);
    }
    
    // Log reviews before sorting
    console.log("BEFORE SORTING - Reviews to be sorted:", filtered.map(review => ({
      source: getReviewSource(review),
      rating: getReviewRating(review),
      timestamp: getReviewTimestamp(review),
      date: new Date(getReviewTimestamp(review)).toLocaleString(),
      text: review.text ? review.text.substring(0, 30) + '...' : 'No text',
      author: review.author_name || review.name || 'Unknown'
    })));
    
    // Apply sorting
    console.log("Applying sort filter:", sortFilter);
    const preSort = [...filtered];
    
    try {
      switch (sortFilter) {
        case 'newest':
          console.log("Sorting by NEWEST");
          filtered.sort((a, b) => {
            const timestampA = getReviewTimestamp(a);
            const timestampB = getReviewTimestamp(b);
            console.log(`Compare newest: ${timestampB} vs ${timestampA} = ${timestampB - timestampA}`);
            return timestampB - timestampA; // Descending order (newest first)
          });
          break;
        case 'oldest':
          console.log("Sorting by OLDEST");
          filtered.sort((a, b) => {
            const timestampA = getReviewTimestamp(a);
            const timestampB = getReviewTimestamp(b);
            console.log(`Compare oldest: ${timestampA} vs ${timestampB} = ${timestampA - timestampB}`);
            return timestampA - timestampB; // Ascending order (oldest first)
          });
          break;
        case 'highest':
          console.log("Sorting by HIGHEST rating");
          filtered.sort((a, b) => {
            const ratingA = getReviewRating(a);
            const ratingB = getReviewRating(b);
            console.log(`Compare highest: ${ratingB} vs ${ratingA} = ${ratingB - ratingA}`);
            return ratingB - ratingA; // Descending order (highest first)
          });
          break;
        case 'lowest':
          console.log("Sorting by LOWEST rating");
          filtered.sort((a, b) => {
            const ratingA = getReviewRating(a);
            const ratingB = getReviewRating(b);
            console.log(`Compare lowest: ${ratingA} vs ${ratingB} = ${ratingA - ratingB}`);
            return ratingA - ratingB; // Ascending order (lowest first)
          });
          break;
        default:
          console.log("Using DEFAULT sort (newest)");
          filtered.sort((a, b) => {
            const timestampA = getReviewTimestamp(a);
            const timestampB = getReviewTimestamp(b);
            console.log(`Compare default: ${timestampB} vs ${timestampA} = ${timestampB - timestampA}`);
            return timestampB - timestampA; // Default to newest first
          });
      }
    } catch (error) {
      console.error("Error sorting reviews:", error);
      // Keep the original order if sorting fails
      filtered = [...preSort];
    }
    
    // Log reviews after sorting
    console.log("AFTER SORTING - Sorted reviews:", filtered.map(review => ({
      source: getReviewSource(review),
      rating: getReviewRating(review),
      timestamp: getReviewTimestamp(review),
      date: new Date(getReviewTimestamp(review)).toLocaleString(),
      text: review.text ? review.text.substring(0, 30) + '...' : 'No text',
      author: review.author_name || review.name || 'Unknown'
    })));
    
    console.log("Computed filtered reviews:", filtered.length);
    console.log("===============================================");
    
    return filtered;
  }, [reviews, reviewFilter, sourceFilter, sortFilter, sortCounter]);

  // Handle going back to search results
  const handleBackToResults = () => {
    console.log('Going back to search results with state:', {
      searchResults,
      visibleResults,
      totalResults,
      selectedService,
      selectedArea,
      selectedSpecific
    });
    handleNavigateToUserPage();
  }

  // Add action handlers
  const handleBookAppointment = () => {
    // Redirect to signup if not logged in
    if (!currentUser) {
      toast.error('Please sign up to book an appointment');
      navigate('/signup', { state: { from: location } });
      return;
    }
    console.log("Book appointment clicked")
    setShowBookingModal(true)
  }

  const handleCloseBookingModal = useCallback(() => {
    setShowBookingModal(false)
  }, [])

  const handleBookingFormChange = useCallback((e) => {
    const { name, value } = e.target
    setBookingForm(prev => ({
      ...prev,
      [name]: value
    }))
  }, [])

  const handleSubmitBooking = async (e, bookingData) => {
    e.preventDefault()
    console.log("Booking submitted:", bookingData)
    
    // Reset the form
    setBookingForm({
      name: '',
      phone: '',
      lawyerId: lawyer?.id,
      lawyerEmail: lawyer?.contactDetails?.email || lawyer?.email || 'info@zahoot.co.uk',
      legalArea: '',
      description: ''
    })
  }

  const handleCallLawyer = () => {
    // Redirect to signup if not logged in
    if (!currentUser) {
      toast.error('Please sign up to call this lawyer');
      navigate('/signup', { state: { from: location } });
      return;
    }
    
    if (
      lawyer &&
      lawyer.contactDetails &&
      lawyer.contactDetails.phone &&
      lawyer.contactDetails.phone !== "Not Available"
    ) {
      window.location.href = `tel:${lawyer.contactDetails.phone}`
    } else {
      console.log("No phone number available")
      // You could add a notification here to inform the user
      alert("Sorry, no phone number is available for this lawyer.")
    }
  }

  // Add refs for review section and add review button
  const reviewSectionRef = useRef(null)
  const addReviewBtnRef = useRef(null)

  const handleLeaveReview = () => {
    if (!currentUser) {
      // Redirect to signup
      toast.error('Please sign up to leave a review');
      navigate('/signup', { state: { from: location } });
      return;
    }
    setShowReviewModal(true);
  };

  // Add handleSubmitReview
  const handleSubmitReview = async (reviewData) => {
    try {
      setSubmittingReview(true);
      await submitReview(lawyer.id, {
        ...reviewData,
        user_id: currentUser.id,
        user_name: currentUser.name || currentUser.username
      });
      
      // Refresh reviews
      const updatedReviews = await fetchReviews(lawyer.id, true);
      setReviews(updatedReviews);
      
      // Close modal and show success message
      setShowReviewModal(false);
      toast.success('Review submitted successfully!');
    } catch (error) {
      console.error('Error submitting review:', error);
      if (error.response?.data?.error === 'You have already reviewed this organisation') {
        toast.error('You have already reviewed this lawyer. You can only submit one review per lawyer.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to submit review. Please try again.');
      }
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleVisitWebsite = () => {
    // Redirect to signup if not logged in
    if (!currentUser) {
      toast.error('Please sign up to visit this lawyer\'s website');
      navigate('/signup', { state: { from: location } });
      return;
    }
    
    if (
      lawyer &&
      lawyer.contactDetails &&
      lawyer.contactDetails.website &&
      lawyer.contactDetails.website !== "Not Available"
    ) {
      const websiteUrl = lawyer.contactDetails.website.startsWith("http")
        ? lawyer.contactDetails.website
        : `https://${lawyer.contactDetails.website}`
      window.open(websiteUrl, "_blank", "noopener,noreferrer")
    } else {
      console.log("No website available")
      // You could add a notification here to inform the user
      alert("Sorry, no website is available for this lawyer.")
    }
  }

  const handleSendEnquiry = () => {
    // Redirect to signup if not logged in
    if (!currentUser) {
      toast.error('Please sign up to send an enquiry');
      navigate('/signup', { state: { from: location } });
      return;
    }
    
    console.log("Send enquiry clicked");
    
    // Show the enquiry modal instead of using mailto
    setShowEnquiryModal(true);
  }

  const handleGetDirections = () => {
    if (
      lawyer &&
      lawyer.contactDetails &&
      lawyer.contactDetails.address &&
      lawyer.contactDetails.city &&
      lawyer.contactDetails.address !== "Not Available" &&
      lawyer.contactDetails.city !== "Not Available"
    ) {
      // Create Google Maps URL using the office address
      const address = encodeURIComponent(`${lawyer.contactDetails.address}, ${lawyer.contactDetails.city}`)
      window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, "_blank", "noopener,noreferrer")
    } else {
      console.log("No address available for directions")
      // You could add a notification here to inform the user
      alert("Sorry, no address is available for directions.")
    }
  }

  // Add function to handle claim listing
  const handleClaimListing = async () => {
    if (!currentUser) {
      // Redirect to login if not logged in
      toast.error('Please log in to claim this listing');
      navigate('/signin', { state: { from: location } });
      return;
    }
    
    if (currentUser.role !== 'legal_professional' && currentUser.role_name !== 'legal_professional') {
      toast.error('Only legal professionals can claim listings');
      return;
    }
    
    // Check if user is already verified for another organization
    if (currentUser.is_claimed) {
      toast.error('You have already claimed another profile. Legal professionals can only claim one organization at a time.');
      return;
    }
    
    // Show the claim modal
    setShowClaimModal(true);
  };

  // Add function to handle claim submission
  const handleClaimSubmission = async (formData) => {
    try {
      setShowClaimModal(false); // Close the modal while processing
      toast.loading('Submitting claim...');
      
      // Check if user has legal_professional role first
      if (currentUser.role !== 'legal_professional' && currentUser.role_name !== 'legal_professional') {
        toast.dismiss();
        toast.error('Your account is not set as a legal professional. Please contact support to update your account type.');
        return;
      }
      
      // Extract organization ID properly
      const orgId = lawyer?.id || lawyer?.organisation_id;
      
      if (!orgId) {
        toast.dismiss();
        toast.error('Could not determine organization ID');
        return;
      }
      
      // First upload verification documents
      const uploadResult = await userService.uploadVerificationDocuments(
        currentUser.id,
        orgId,
        formData
      );
      
      console.log('Upload result:', uploadResult);
      
      // Then submit the claim
      await userService.setClaimedProfile(currentUser.id, {
        is_claimed: true,
        organisation_id: orgId,
        verification_documents: {
          document_type: formData.documentType,
          additional_info: formData.additionalInfo
        }
      });
      
      toast.dismiss();
      toast.success('Listing claimed successfully! Your claim is pending approval.');
      
      // Refresh the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error claiming listing:', error);
      toast.dismiss();
      
      // Handle specific error codes
      if (error.response && error.response.status === 409) {
        // 409 Conflict - Already claimed or organization is already verified
        toast.error(error.response.data.error || 'You cannot claim this listing. It may already be verified or you already have a claimed profile.');
      } else {
        toast.error(error.message || 'Failed to claim listing');
      }
    }
  };

  // Add function to check if user can review
  const canUserReview = () => {
    console.log('Current User State:', currentUser);
    console.log('Reviews State:', reviews);
    
    if (!currentUser) {
      console.log('No current user');
      return false;
    }
    if (currentUser.role !== 'user' && currentUser.role_name !== 'user') {
      console.log('User is not a regular user');
      return false;
    }
    if (lawyer?.user_id === currentUser.id) {
      console.log('User is reviewing their own profile');
      return false;
    }
    
    // Check if user has already reviewed
    const hasReviewed = reviews.reviews?.some(review => {
      const isZahootReview = review.source === 'zahoot' || !review.source;
      const isCurrentUser = review.user_id === currentUser.id;
      console.log('Review check:', { review, isZahootReview, isCurrentUser });
      return isZahootReview && isCurrentUser;
    });
    
    console.log('Has user already reviewed:', hasReviewed);
    return !hasReviewed;
  };

  // Fetch current user on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        console.log('Token found:', !!token);
        if (token) {
          // Set the Authorization header for all requests
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Always fetch fresh data first
          try {
            const user = await userService.getProfile();
            console.log('Fetched fresh user data:', user);
            
            // Update local storage with fresh data
            localStorage.setItem('user', JSON.stringify(user));
            
            setCurrentUser(user);
            return; // Exit if we successfully got fresh data
          } catch (fetchError) {
            console.error('Error fetching fresh user data:', fetchError);
            // Continue to fallback if fetch fails
          }
          
          // Fallback to stored user data
          const storedUser = JSON.parse(localStorage.getItem('user'));
          if (storedUser) {
            console.log('Using stored user data:', storedUser);
            setCurrentUser(storedUser);
          }
        }
      } catch (error) {
        console.error('Error in fetchCurrentUser:', error);
        // Final fallback to stored user data
        const storedUser = JSON.parse(localStorage.getItem('user'));
        if (storedUser) {
          console.log('Falling back to stored user:', storedUser);
          setCurrentUser(storedUser);
        }
      }
    };
    fetchCurrentUser();
  }, []);

  // Add a refreshReviews function that will fetch the latest reviews
  const refreshReviews = async () => {
    if (lawyer && lawyer.id) {
      try {
        console.log(`Refreshing reviews for lawyer ID: ${lawyer.id}`);
        const updatedReviews = await fetchReviews(lawyer.id, true); // Force bypass cache
        setReviews(updatedReviews || { reviews: [], stats: null });
        return updatedReviews;
      } catch (error) {
        console.error("Error refreshing reviews:", error);
        toast.error("Failed to refresh reviews. Please try again.");
        return null;
      }
    }
    return null;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="lawyer-profile-page">
        <SEO title="Loading Profile - Zahoot" />
        
        {/* Replace Sidebar with AppSidebar */}
        <AppSidebar userType="lawyer" onNavigateToUserPage={handleNavigateToUserPage} />

        {/* Main Content with loading spinner */}
        <main className="lawyer-profile-page__main">
          {/* Search Dropdowns - placeholder */}
          <div className="lawyer-profile-page__search-dropdowns">
            <div className="lawyer-profile-page__dropdown-container">
              <div className="lawyer-profile-page__custom-dropdown"></div>
            </div>
            <div className="lawyer-profile-page__dropdown-container">
              <div className="lawyer-profile-page__custom-dropdown"></div>
            </div>
            <div className="lawyer-profile-page__dropdown-container">
              <div className="lawyer-profile-page__custom-dropdown"></div>
            </div>
          </div>

          {/* Loading container */}
          <div className="profile-container bg-white rounded-3xl shadow-lg overflow-hidden flex items-center justify-center" style={{ minHeight: "60vh" }}>
            <div className="text-center p-12">
              <div className="lawyer-profile-page__spinner mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Loading profile...</p>
            </div>
          </div>
        </main>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="lawyer-profile-page">
        <SEO title="Error - Zahoot" />
        
        {/* Replace Sidebar with AppSidebar */}
        <AppSidebar userType="lawyer" onNavigateToUserPage={handleNavigateToUserPage} />

        {/* Main Content with error message */}
        <main className="lawyer-profile-page__main">
          {/* Search Dropdowns - placeholder */}
          <div className="lawyer-profile-page__search-dropdowns">
            <div className="lawyer-profile-page__dropdown-container">
              <div className="lawyer-profile-page__custom-dropdown"></div>
            </div>
            <div className="lawyer-profile-page__dropdown-container">
              <div className="lawyer-profile-page__custom-dropdown"></div>
            </div>
            <div className="lawyer-profile-page__dropdown-container">
              <div className="lawyer-profile-page__custom-dropdown"></div>
            </div>
          </div>

          {/* Error container */}
          <div className="profile-container bg-white rounded-3xl shadow-lg overflow-hidden flex items-center justify-center" style={{ minHeight: "60vh" }}>
            <div className="text-center p-12">
              <div className="rounded-full bg-red-100 p-3 mx-auto mb-6 w-16 h-16 flex items-center justify-center">
                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Error</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-full transition-colors shadow-sm"
              >
                Try Again
              </button>
              <button 
                onClick={handleBackToResults} 
                className="px-6 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-full transition-colors shadow-sm ml-4"
              >
                Back to Search Results
              </button>
            </div>
          </div>
        </main>
      </div>
    )
  }

  // If no lawyer data was found
  if (!lawyer) {
    return (
      <div className="lawyer-profile-page">
        <SEO title="Lawyer Not Found - Zahoot" />
        
        {/* Replace Sidebar with AppSidebar */}
        <AppSidebar userType="lawyer" onNavigateToUserPage={handleNavigateToUserPage} />

        {/* Main Content with not found message */}
        <main className="lawyer-profile-page__main">
          {/* Search Dropdowns - placeholder */}
          <div className="lawyer-profile-page__search-dropdowns">
            <div className="lawyer-profile-page__dropdown-container">
              <div className="lawyer-profile-page__custom-dropdown"></div>
            </div>
            <div className="lawyer-profile-page__dropdown-container">
              <div className="lawyer-profile-page__custom-dropdown"></div>
            </div>
            <div className="lawyer-profile-page__dropdown-container">
              <div className="lawyer-profile-page__custom-dropdown"></div>
            </div>
          </div>

          {/* Not Found container */}
          <div className="profile-container bg-white rounded-3xl shadow-lg overflow-hidden flex items-center justify-center" style={{ minHeight: "60vh" }}>
            <div className="text-center p-12">
              <div className="rounded-full bg-indigo-100 p-3 mx-auto mb-6 w-16 h-16 flex items-center justify-center">
                <svg className="w-10 h-10 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Lawyer Not Found</h2>
              <p className="text-gray-600 mb-6">We couldn't find the lawyer profile you're looking for.</p>
              <button 
                onClick={handleBackToResults} 
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-full transition-colors shadow-sm"
              >
                Back to Search Results
              </button>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="lawyer-profile-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <SEO 
        fallbackTitle={generateSeoTitle(lawyer, isSearchMode)}
        canonicalUrl={!isSearchMode ? getCanonicalUrl(lawyer) : null}
        structuredData={!isSearchMode ? generateLawyerStructuredData(lawyer) : null}
        type={!isSearchMode ? "LegalService" : "SearchResultsPage"}
        imageUrl={!isSearchMode ? lawyer?.teamPhoto : null}
      />

      {/* Replace Sidebar with AppSidebar */}
      <AppSidebar userType="lawyer" onNavigateToUserPage={handleNavigateToUserPage} />

      {/* Main Content */}
      <main className="lawyer-profile-page__main">
        {/* Search Dropdowns */}
        <div className="lawyer-profile-page__search-container">
          <SearchDropdowns
            selectedService={selectedService}
            selectedArea={selectedArea}
            selectedSpecific={selectedSpecific}
            serviceOptions={serviceOptions}
            areaOptions={areaOptions}
            specificOptions={specificOptions}
            onServiceChange={handleServiceChange}
            onAreaChange={handleAreaChange}
            onSpecificChange={handleSpecificChange}
            isClearable={true}
          />
          <button 
            className="bg-gradient-to-r from-[#D80364] to-[#F0654B] text-white font-medium py-3 px-6 sm:py-4 sm:px-8 rounded-full shadow-md hover:shadow-lg hover:from-[#C50355] hover:to-[#E0553B] transition-all duration-300 flex items-center justify-center w-full sm:w-auto min-h-[56px] sm:min-h-[64px]"
            onClick={handleSolicitorSearch}
            disabled={isSearching}
            aria-label="Search"
          >
            {isSearching ? (
              <span className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            ) : (
              <svg className="w-6 h-6 sm:w-8 sm:h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
                <line x1="16.018" y1="16.485" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        </div>

        {/* Profile or Search Results Container */}
        <div className="profile-container bg-white rounded-3xl shadow-lg overflow-hidden">
          {isSearchMode ? (
            <div className="search-results-container p-8">
              <div className="search-results-header mb-6 flex justify-between items-center border-b border-gray-200 pb-4">
                <h2 className="text-2xl font-semibold text-gray-800">
                  {visibleResults.length > 0 ? 
                    `${totalResults} Solicitors Found` : 
                    "Search Results"}
                </h2>
                <button 
                  onClick={handleClearSearch}
                  className="px-4 py-2 text-sm bg-indigo-50 text-indigo-700 rounded-md hover:bg-indigo-100 transition-colors"
                >
                  Back to Profile
                </button>
              </div>

              {isSearching ? (
                <div className="text-center p-12">
                  <div className="lawyer-profile-page__spinner mx-auto mb-4"></div>
                  <p className="text-gray-600 text-lg">Searching for solicitors...</p>
                </div>
              ) : error ? (
                <div className="text-center p-12">
                  <div className="rounded-full bg-red-100 p-3 mx-auto mb-6 w-16 h-16 flex items-center justify-center">
                    <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                    </svg>
                  </div>
                  <p className="text-gray-600 mb-6">{error}</p>
                </div>
              ) : visibleResults.length > 0 ? (
                <>
                  <div className="search-results__grid">
                    {visibleResults.map((solicitor) => {
                      // Find the selected service in practice areas if it exists
                      let service = 'General Practice';
                      
                      if (solicitor.practice_areas && Array.isArray(solicitor.practice_areas) && solicitor.practice_areas.length > 0) {
                        if (selectedService?.label) {
                          // Look for exact match with selectedService
                          const matchingArea = solicitor.practice_areas.find(pa => {
                            const areaName = typeof pa === 'object' ? pa.name : pa;
                            return areaName.toLowerCase() === selectedService.label.toLowerCase();
                          });
                          
                          if (matchingArea) {
                            service = typeof matchingArea === 'object' ? matchingArea.name : matchingArea;
                          } else {
                            // Default to first area if no match found
                            const firstArea = solicitor.practice_areas[0];
                            service = typeof firstArea === 'object' ? firstArea.name : firstArea;
                          }
                        } else {
                          // No selectedService, use first area
                          const firstArea = solicitor.practice_areas[0];
                          service = typeof firstArea === 'object' ? firstArea.name : firstArea;
                        }
                      } else if (typeof solicitor.practice_areas === 'string') {
                        try {
                          const parsedAreas = JSON.parse(solicitor.practice_areas);
                          if (Array.isArray(parsedAreas) && parsedAreas.length > 0) {
                            service = typeof parsedAreas[0] === 'object' ? parsedAreas[0].name : parsedAreas[0];
                          }
                        } catch (e) {
                          console.error('Error parsing practice areas:', e);
                        }
                      }
                      
                      return (
                        <LawyerCard 
                          key={solicitor.id}
                          lawyer={{
                            id: solicitor.id,
                            name: solicitor.practice_name,
                            firm: solicitor.practice_name,
                            location: solicitor.offices?.[0]?.town || 'Location not provided',
                            rating: solicitor.average_rating || null,
                            reviews: solicitor.review_count || null,
                            service: service,
                            isOnline: false,
                            photo: null,
                            isVerified: Boolean(solicitor.is_verified),
                            teamPhoto: solicitor.team_photo || null,
                            orgId: solicitor.id
                          }}
                          onClick={() => handleSelectSolicitor({
                            id: solicitor.id,
                            name: solicitor.practice_name,
                            location: solicitor.offices?.[0]?.town || 'Location not provided'
                          })}
                        />
                      );
                    })}
                  </div>
                  
                  {visibleResults.length < searchResults.length && (
                    <div className="search-results__load-more">
                      <button 
                        className="search-results__load-more-btn"
                        onClick={handleLoadMore}
                      >
                        Load More Results
                      </button>
                    </div>
                  )}
                </>
              ) : searchPerformed && (
                <div className="text-center p-12">
                  <div className="rounded-full bg-indigo-100 p-3 mx-auto mb-6 w-16 h-16 flex items-center justify-center">
                    <svg className="w-10 h-10 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">No Results Found</h2>
                  <p className="text-gray-600 mb-6">Try adjusting your search criteria or select a different service area.</p>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Profile Header */}
              <ProfileHeader
                lawyer={lawyer}
                reviews={reviews}
                onBookAppointment={handleBookAppointment}
                onCallLawyer={handleCallLawyer}
                onSendEnquiry={handleSendEnquiry}
                onLeaveReview={handleLeaveReview}
                onVisitWebsite={handleVisitWebsite}
                onClaimListing={handleClaimListing}
                currentUser={currentUser}
                canClaimListing={!lawyer?.isVerified && (!currentUser?.is_claimed)}
              />

              {/* Information Cards */}
              <div className="p-8">
                <InformationCards lawyer={lawyer} />

                {/* Contact and Overview Section */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* Contact Details */}
                  <div className="md:col-span-1">
                    <ContactSection lawyer={lawyer} onGetDirections={handleGetDirections} currentUser={currentUser} />
                  </div>

                  {/* Overview and Reviews */}
                  <div className="md:col-span-2">
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow h-full">
                      {/* Overview Section */}
                      {lawyer?.overview && (
                        <div className="mb-8">
                          <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                            <svg
                              className="w-5 h-5 mr-2 text-indigo-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              ></path>
                            </svg>
                            About {lawyer.name}
                          </h3>
                          <p className="text-gray-600 leading-relaxed">{lawyer.overview}</p>
                        </div>
                      )}

                      {/* Reviews Section */}
                      <ReviewsSection
                        reviews={reviews}
                        filteredReviews={filteredReviews}
                        reviewFilter={reviewFilter}
                        sourceFilter={sourceFilter}
                        sortFilter={sortFilter}
                        isSorting={isSorting}
                        currentUser={currentUser}
                        canUserReview={canUserReview()}
                        lawyer={lawyer}
                        onLeaveReview={handleLeaveReview}
                        onClaimListing={handleClaimListing}
                        canClaimListing={!lawyer?.isVerified && (!currentUser?.is_claimed)}
                        reviewSectionRef={reviewSectionRef}
                        addReviewBtnRef={addReviewBtnRef}
                        setReviewFilter={setReviewFilter}
                        setSourceFilter={setSourceFilter}
                        setSortFilter={setSortFilter}
                        setSortCounter={setSortCounter}
                        refreshReviews={refreshReviews}
                      />
                    </div>
                  </div>
                </div>

                {/* Call to Action Section */}
                <CallToAction 
                  lawyerName={lawyer?.name} 
                  onBookAppointment={handleBookAppointment} 
                  onSendEnquiry={handleSendEnquiry} 
                />
              </div>
            </>
          )}
        </div>
      </main>

      {/* Booking Appointment Modal */}
      <BookingModal 
        showModal={showBookingModal}
        lawyerName={lawyer?.name}
        bookingForm={bookingForm}
        onClose={handleCloseBookingModal}
        onChange={handleBookingFormChange}
        onSubmit={handleSubmitBooking}
        userData={currentUser}
      />
      
      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        onSubmit={handleSubmitReview}
        lawyerName={lawyer?.name}
      />

      {/* Claim Listing Modal */}
      <ClaimListingModal
        isOpen={showClaimModal}
        onClose={() => setShowClaimModal(false)}
        onSubmit={handleClaimSubmission}
      />
      
      {/* Add EnquiryModal */}
      <EnquiryModal
        isOpen={showEnquiryModal}
        onClose={() => setShowEnquiryModal(false)}
        lawyer={lawyer}
        userData={currentUser}
      />
    </div>
  )
}

export default LawyerProfilePage

// Helper function to generate proper SEO title for solicitor profiles
function generateSeoTitle(lawyer, isSearchMode) {
  if (isSearchMode) {
    return "Search Solicitors - Zahoot";
  }
  
  if (!lawyer) {
    return "Lawyer Profile - Zahoot";
  }
  
  // Use practice name (firm name) as the base
  const firmName = lawyer.name;
  const location = lawyer.location || (lawyer.rawData && lawyer.rawData.city);
  
  if (firmName && location) {
    // Format: "Shakespeare Martineau | Birmingham"
    return `${firmName} | ${location}`;
  } else if (firmName) {
    // If no location, just use firm name
    return firmName;
  } else {
    return "Lawyer Profile - Zahoot";
  }
}

// Helper function to generate canonical URL for SEO
function getCanonicalUrl(lawyer) {
  if (!lawyer || !lawyer.rawData) return null;
  
  const data = lawyer.rawData;
  const baseUrl = window.location.origin;
  
  // FIXED: Create a consistent slug generation function
  const createSlug = (text) => {
    if (!text) return '';
    return text.toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };
  
  // If we have a primary category and city, use the most specific URL format
  if (data.categories && data.categories.length > 0 && data.city) {
    // Check if category is an object with name property or a string
    const primaryCategory = typeof data.categories[0] === 'object' && data.categories[0]?.name
      ? createSlug(data.categories[0].name)
      : typeof data.categories[0] === 'string'
        ? createSlug(data.categories[0])
        : 'legal-services';
        
    const citySlug = createSlug(data.city);
    const nameSlug = createSlug(data.name || data.slug);
    
    return `${baseUrl}/${primaryCategory}/solicitor/${citySlug}/${nameSlug}`;
  }
  
  // If we have city but no category
  if (data.city) {
    const citySlug = createSlug(data.city);
    const nameSlug = createSlug(data.name || data.slug);
    
    return `${baseUrl}/solicitor/${citySlug}/${nameSlug}`;
  }
  
  // Fallback to basic URL
  const nameSlug = createSlug(data.name || data.slug);
  return `${baseUrl}/solicitor/${nameSlug}`;
}

// Function to generate structured data markup for lawyers
function generateLawyerStructuredData(lawyer) {
  if (!lawyer) return null;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "name": lawyer.name,
    "description": lawyer.overview || `Legal services provided by ${lawyer.name}`,
    "url": window.location.href
  };
  
  // Add address if available
  if (lawyer.contactDetails && lawyer.contactDetails.address && lawyer.contactDetails.city) {
    structuredData.address = {
      "@type": "PostalAddress",
      "streetAddress": lawyer.contactDetails.address,
      "addressLocality": lawyer.contactDetails.city.split(' ')[0] // Remove postcode if present
    };
  }
  
  // Add telephone if available
  if (lawyer.contactDetails && lawyer.contactDetails.phone) {
    structuredData.telephone = lawyer.contactDetails.phone;
  }
  
  // Add image if available
  if (lawyer.teamPhoto) {
    structuredData.image = lawyer.teamPhoto;
  }
  
  // Add sameAs for website if available
  if (lawyer.contactDetails && lawyer.contactDetails.website) {
    structuredData.sameAs = [lawyer.contactDetails.website];
  }
  
  // Add aggregateRating if available
  if (lawyer.rating) {
    structuredData.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": lawyer.rating,
      "reviewCount": lawyer.reviews || 0
    };
  }
  
  // Add services/areas of practice if available
  if (lawyer.practiceAreas && lawyer.practiceAreas.length > 0) {
    structuredData.serviceType = lawyer.practiceAreas;
  }
  
  return structuredData;
}

